import { ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { combineLatest, forkJoin, merge, Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CategoriesService, CategoryStreams } from '../../../services/accounting/categories.service';
import { ChartOfAccountsService, ChartOfAccountsStreams, COAAccount } from '../../../services/accounting/chart-of-accounts.service';
import { LegFiJwtService } from '../../../services/auth/legfi-jwt.service';
import { Category, CATEGORY_EXPENSE_ACCOUNT_TYPE, CATEGORY_INCOME_ACCOUNT_TYPE } from '../../../models/entities/accounting/category';
import { FixedAsset } from '../../../models/entities/accounting/fixed-asset';
import { Liability } from '../../../models/entities/accounting/liability';
import { Equity } from '../../../models/entities/accounting/equity';

type PickableAccount = Category | FixedAsset | Liability | Equity;

const ALL_KEY = '__all';

interface PhSearchSelectItem {
    value: string;
    label: string;
    group?: string;
    level?: number;
    hint?: string;
    badge?: string;
    disabled?: boolean;
}

interface PhSearchSelectToggle {
    key: string;
    label: string;
    checked?: boolean;
}

/** Kit `ph-search-select` COA/category picker; the control holds the account object, keyed `accountType:id` for the element. */
@UntilDestroy()
@Component({
    selector: 'app-category-picker',
    templateUrl: './category-picker.component.html',
    standalone: true,
    imports: [],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CategoryPickerComponent implements OnInit, OnChanges
{
    @Input() formGroup: UntypedFormGroup;
    @Input() controlName = 'expenseCategory';
    @Input() organizationId: number = null;
    @Input() placeholder = 'Select a category';
    @Input() hasError = false;
    @Input() showRevenueCategories = false;
    @Input() showExpenseCategories = true;
    @Input() showAssets = false;
    @Input() showLiabilities = false;
    @Input() showEquities = false;
    @Input() allLabel: string | null = null;

    @Output() loadstart = new EventEmitter<void>();
    @Output() loadeddata = new EventEmitter<void>();

    items: PhSearchSelectItem[] = [];
    toggles: PhSearchSelectToggle[] = [];
    selectedKey: string | null = null;
    disabled = false;

    private _controlSub: Subscription | null = null;
    private _categoryStreams: CategoryStreams;
    private _coaStreams: ChartOfAccountsStreams;
    private _valueMap = new Map<string, PickableAccount>();

    private _categories: Category[] = [];
    private _liabilities: Liability[] = [];
    private _equities: Equity[] = [];
    private _assets: FixedAsset[] = [];

    private _showDeleted = false;

    constructor(
            private _categoriesService: CategoriesService,
            private _chartOfAccountsService: ChartOfAccountsService,
            private _fb: UntypedFormBuilder,
            private _cdr: ChangeDetectorRef,
    ) {
    }

    ngOnInit(): void {
        if (!this.formGroup) {
            this.formGroup = this._fb.group({ [this.controlName]: [null] });
        }

        if (this.organizationId === null) {
            const jwt = LegFiJwtService.read();
            this.organizationId = jwt.orgId;
        }

        this._categoryStreams = this._categoriesService.getOrganizationStreams(this.organizationId);
        this._coaStreams = this._chartOfAccountsService.getOrganizationStreams(this.organizationId);

        combineLatest([
            this._categoryStreams.$categories,
            this._coaStreams.liabilities.$data,
            this._coaStreams.equities.$data,
            this._coaStreams.assets.$data,
        ]).pipe(untilDestroyed(this)).subscribe(([categories, liabilities, equities, assets]) => {
            this._categories = categories;
            this._liabilities = liabilities;
            this._equities = equities;
            this._assets = assets;
            this.updateToggles();
            this.rebuildItems();
        });

        this.bindControl();

        this.loadOptions();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const rebound = changes['formGroup'] || changes['controlName'];
        if (rebound && !rebound.firstChange && this.formGroup) {
            this.bindControl();
            this.rebuildItems();
        }
    }

    onPick(value: string): void {
        const account = value === ALL_KEY ? null : this._valueMap.get(value);
        if (account === undefined) {
            return;
        }
        const control = this.formGroup.get(this.controlName);
        control.markAsTouched();
        control.markAsDirty();
        control.setValue(account);
    }

    onToggle(change: { key: string; checked: boolean }): void {
        if (change.key === 'deleted') {
            this._showDeleted = change.checked;
        }
        this.updateToggles();
        this.rebuildItems();
    }

    private bindControl(): void {
        this._controlSub?.unsubscribe();
        const control = this.formGroup.get(this.controlName);
        this.disabled = !!control?.disabled;
        this._controlSub = control
            ? merge(
                control.valueChanges.pipe(tap(() => this.rebuildItems())),
                control.statusChanges.pipe(tap(() => {
                    this.disabled = control.disabled;
                    this._cdr.markForCheck();
                })),
            ).pipe(untilDestroyed(this)).subscribe()
            : null;
    }

    private loadOptions(): void {
        const toAwait: Observable<unknown>[] = [];

        if ((this.showRevenueCategories || this.showExpenseCategories) && !this._categoryStreams.$loaded.getValue()) {
            toAwait.push(this._categoriesService.getAllCategories(this.organizationId));
        }

        if (this.showAssets && !this._coaStreams.assets.$loaded.getValue()) {
            toAwait.push(this._chartOfAccountsService.getAssets(this.organizationId));
        }

        if (this.showLiabilities && !this._coaStreams.liabilities.$loaded.getValue()) {
            toAwait.push(this._chartOfAccountsService.getLiabilities(this.organizationId));
        }

        if (this.showEquities && !this._coaStreams.equities.$loaded.getValue()) {
            toAwait.push(this._chartOfAccountsService.getEquities(this.organizationId));
        }

        if (toAwait.length === 0) {
            return;
        }

        this.loadstart.emit();
        forkJoin(toAwait).pipe(untilDestroyed(this)).subscribe({
            next: () => this.loadeddata.emit(),
            error: () => this.loadeddata.emit(),
        });
    }

    private updateToggles(): void {
        const hasDeleted = this._categories.some(c => c.deletedAt !== null)
                || this._liabilities.some(l => l.deletedAt !== null)
                || this._equities.some(e => e.deletedAt !== null)
                || this._assets.some(a => a.deletedAt !== null);

        const toggles: PhSearchSelectToggle[] = [];
        if (hasDeleted) {
            toggles.push({ key: 'deleted', label: 'Show deleted', checked: this._showDeleted });
        }
        this.toggles = toggles;
    }

    private rebuildItems(): void {
        const map = new Map<string, PickableAccount>();
        const items: PhSearchSelectItem[] = [];
        const currentValue = this.formGroup?.get(this.controlName)?.value as PickableAccount ?? null;

        if (this.allLabel) {
            items.push({ value: ALL_KEY, label: this.allLabel });
        }

        if (this.showRevenueCategories) {
            this.addCategories(
                    this._categories.filter(c => c.type === CATEGORY_INCOME_ACCOUNT_TYPE),
                    'Income', map, items, currentValue);
        }

        if (this.showExpenseCategories) {
            this.addCategories(
                    this._categories.filter(c => c.type === CATEGORY_EXPENSE_ACCOUNT_TYPE),
                    'Expense', map, items, currentValue);
        }

        if (this.showAssets) {
            this.addFlatAccounts(this._assets, 'Assets', map, items, currentValue);
        }

        if (this.showLiabilities) {
            this.addFlatAccounts(this._liabilities, 'Liabilities', map, items, currentValue);
        }

        if (this.showEquities) {
            this.addFlatAccounts(this._equities, 'Equities', map, items, currentValue);
        }

        this._valueMap = map;
        this.items = items;
        this.selectedKey = currentValue ? this.keyFor(currentValue) : (this.allLabel ? ALL_KEY : null);
        this._cdr.markForCheck();
    }

    private addCategories(
            categories: Category[],
            sectionLabel: string,
            map: Map<string, PickableAccount>,
            items: PhSearchSelectItem[],
            currentValue: PickableAccount | null,
    ): void {
        for (const category of categories) {
            const children = category.children ?? [];
            const visibleChildren = children.filter(child => this.isVisible(child) || this.isCurrent(child, currentValue));
            if (this.isVisible(category) || this.isCurrent(category, currentValue) || visibleChildren.length > 0) {
                this.addItem(category, sectionLabel, map, items, 1);
            }
            for (const child of visibleChildren) {
                this.addItem(child, sectionLabel, map, items, 2);
            }
        }
    }

    private addFlatAccounts(
            accounts: (FixedAsset | Liability | Equity)[],
            sectionLabel: string,
            map: Map<string, PickableAccount>,
            items: PhSearchSelectItem[],
            currentValue: PickableAccount | null,
    ): void {
        for (const account of accounts) {
            if (this.isVisible(account) || this.isCurrent(account, currentValue)) {
                this.addItem(account, sectionLabel, map, items, 1);
            }
        }
    }

    private addItem(account: PickableAccount, group: string, map: Map<string, PickableAccount>, items: PhSearchSelectItem[], level: number): void {
        const key = this.keyFor(account);
        map.set(key, account);
        items.push({
            value: key,
            label: account.label,
            group,
            level,
            hint: this.hintFor(account),
            badge: account.deletedAt ? 'Deleted' : undefined,
        });
    }

    private isVisible(account: { deletedAt: unknown }): boolean {
        if (account.deletedAt && !this._showDeleted) {
            return false;
        }
        return true;
    }

    private isCurrent(account: COAAccount, current: COAAccount | null): boolean {
        return !!current && !!account && current.accountType === account.accountType && current.id === account.id;
    }

    private keyFor(account: COAAccount): string {
        return `${account.accountType}:${account.id}`;
    }

    private hintFor(account: PickableAccount): string | undefined {
        const withCode = account as unknown as { accountNumber?: string; number?: string; code?: string };
        const code = withCode.accountNumber || withCode.number || withCode.code || undefined;
        return code || undefined;
    }
}
